/**
 * Desktop system-audio (loopback) helpers — the thin layer over the Tauri
 * capture commands the hosted SPA drives in the desktop shell.
 *
 * Windows exposes many render endpoints (speakers, headphones, virtual-mixer
 * channels like "Sonar — Media"); macOS exposes one global output. We pick the
 * endpoint the meeting is actually playing on by peak level, and keep watching so
 * we can switch if audio later appears on a different output (e.g. the user hit
 * record before starting the call). All functions no-op (return []) off-desktop.
 */
import { IS_TAURI } from "./runtime-origin"

export interface ITapTarget {
  id: string
  name: string
  is_default: boolean
}

export interface ITapLevel {
  id: string
  /** Peak amplitude during the probe window, 0.0 (silent) … 1.0 (loud). */
  peak: number
}

/** Lifecycle of the remote (system-audio) capture, for the on-screen indicator. */
export type TRemotePhase = "idle" | "detecting" | "capturing"

/** Below this, an output is treated as silent. */
export const SILENT_THRESHOLD = 0.005
/**
 * At/above this (sustained over a couple of probes), an output is treated as
 * actively playing audio and becomes an auto-lock candidate.
 *
 * Kept low on purpose: real-world playback peaks can sit well under 0.02 at
 * moderate system/app volume, and the picker's meter is sqrt-boosted
 * (`sqrt(peak)*1.6`) so it shows clear movement down here — which is why a user
 * can pick the right output by eye even when the raw peak is ~0.01. Auto-detect
 * must match that sensitivity or it sits just under the trigger and never locks.
 * The LOCK_TICKS sustain requirement rejects transient blips at this level, and
 * it stays comfortably above SILENT_THRESHOLD (the noise floor).
 */
export const ACTIVE_THRESHOLD = 0.008

async function tauriInvoke<T>(cmd: string, args?: Record<string, unknown>): Promise<T | null> {
  if (!IS_TAURI) return null
  try {
    const { invoke } = await import("@tauri-apps/api/core")
    return await invoke<T>(cmd, args)
  } catch (err) {
    console.info(`[system-audio] ${cmd} failed (older shell or off-desktop?)`, err)
    return null
  }
}

/** Enumerate the system-audio outputs (render endpoints). */
export async function listTapTargets(): Promise<ITapTarget[]> {
  return (await tauriInvoke<ITapTarget[]>("list_tap_targets")) ?? []
}

/** Sample each output's peak level over `durMs` (no capture stream opened). */
export async function probeTapLevels(durMs = 1000): Promise<ITapLevel[]> {
  return (await tauriInvoke<ITapLevel[]>("probe_tap_levels", { durMs })) ?? []
}

/** The loudest level in a probe, or null if the list is empty. */
export function loudest(levels: ITapLevel[]): ITapLevel | null {
  return levels.reduce<ITapLevel | null>((best, l) => (l.peak > (best?.peak ?? -1) ? l : best), null)
}

/** Friendly name for a target id (falls back to the id). */
export function targetName(targets: ITapTarget[], id: string | undefined): string {
  if (!id) return "system output"
  return targets.find((t) => t.id === id)?.name ?? id
}
